import deJson from './locales/de.json';
import enJson from './locales/en.json';

type LocaleStrings = Record<string, string>;

export interface LanguageOption {
  code: string;
  label: string;
  flag?: string;
}

// EN_BASE / DE_BASE are sourced entirely from the JSON locale files
// (./locales/en.json, ./locales/de.json) — the single source of truth for
// the bundled German + English strings. No inline overrides here anymore;
// add or edit keys in the JSON files. The casts keep the rest of this module
// (withEnglish, t, loadLocale, …) working against a flat LocaleStrings map.
const EN_BASE: LocaleStrings = enJson as LocaleStrings;
const DE_BASE: LocaleStrings = deJson as LocaleStrings;

function withEnglish(overrides: LocaleStrings): LocaleStrings {
  return { ...EN_BASE, ...overrides };
}

const locales: Record<string, LocaleStrings> = {
  en: EN_BASE,
  de: DE_BASE,
  zh: withEnglish({
    'widget.widgetLang': '小组件语言',
    'widget.title': '无障碍',
    'widget.subtitle': '调整此页面的对比度、文字和导航',
    'widget.open': '打开无障碍设置',
    'widget.close': '关闭',
    'widget.reset': '全部重置',
    'widget.resetConfirm': '已重置所有调整',
    'widget.poweredBy': '由 Accessify-Widget 提供支持',
    'widget.statement': '无障碍声明',
    'widget.language': '语言',
    'status.active': '已启用 {count} 项',
    'status.noActive': '当前未启用调整',
    'status.activated': '已启用',
    'status.deactivated': '已关闭',
    'section.essentials': '最常用',
    'section.essentials.desc': '先从对比度、文字大小和键盘支持开始。',
    'section.support': '阅读与导航',
    'section.support.desc': '用于聚焦、减少动画和查看页面结构的工具。',
    'section.ai': 'AI 辅助',
    'section.ai.desc': '简化内容、生成替代文本并扫描当前页面。',
    'control.mostUsed': '最常用',
    'control.chooseMode': '选择模式',
    'control.default': '默认',
    'contrast.mode.light': '浅色高对比',
    'contrast.mode.dark': '深色高对比',
    'contrast.mode.high': '高对比',
    'feature.contrast': '对比度',
    'feature.textSize': '更大文字',
    'feature.keyboardNav': '键盘导航',
    'feature.focusHighlight': '焦点高亮',
    'feature.linkHighlight': '突出链接',
    'feature.animationStop': '停止动画',
    'feature.readingGuide': '阅读指引线',
    'feature.readingMask': '阅读遮罩',
    'feature.hideImages': '隐藏图片',
    'feature.bigCursor': '大号光标',
    'feature.pageStructure': '页面结构',
    'feature.tts': '朗读',
    'feature.textSimplify': '简化文本',
    'feature.altText': '图片描述',
    'feature.autoScan': 'WCAG 扫描',
    'section.profiles': '快速配置',
    'profile.visionImpaired': '视力障碍',
    'profile.visionImpaired.desc': '高对比度、更大文字和光标',
    'profile.motorImpaired': '运动障碍',
    'profile.motorImpaired.desc': '键盘导航和焦点辅助',
    'profile.cognitive': '认知支持',
    'profile.cognitive.desc': '阅读辅助、间距调整和简化字体',
    'profile.seizureSafe': '癫痫安全',
    'profile.seizureSafe.desc': '停止动画并减少刺激',
    'profile.adhdFriendly': '注意力友好',
    'profile.adhdFriendly.desc': '减少干扰并提高专注力',
  }),
  hi: withEnglish({
    'widget.widgetLang': 'विजेट भाषा',
    'widget.title': 'सुलभता',
    'widget.subtitle': 'इस पेज पर कंट्रास्ट, टेक्स्ट और नेविगेशन समायोजित करें',
    'widget.open': 'सुलभता सेटिंग खोलें',
    'widget.close': 'बंद करें',
    'widget.reset': 'सब रीसेट करें',
    'widget.resetConfirm': 'सभी बदलाव रीसेट हो गए',
    'widget.poweredBy': 'Accessify-Widget द्वारा संचालित',
    'widget.statement': 'एक्सेसिबिलिटी स्टेटमेंट',
    'widget.language': 'भाषा',
    'status.active': '{count} सक्रिय',
    'status.noActive': 'कोई बदलाव सक्रिय नहीं',
    'status.activated': 'सक्रिय',
    'status.deactivated': 'निष्क्रिय',
    'section.essentials': 'सबसे ज्यादा उपयोग',
    'section.essentials.desc': 'कंट्रास्ट, टेक्स्ट साइज और कीबोर्ड सहायता से शुरू करें।',
    'section.support': 'पढ़ना और नेविगेशन',
    'section.support.desc': 'फोकस, कम मोशन और पेज स्ट्रक्चर के लिए टूल।',
    'section.ai': 'AI सहायता',
    'section.ai.desc': 'कंटेंट सरल करें, ऑल्ट टेक्स्ट बनाएं और मौजूदा पेज स्कैन करें।',
    'control.mostUsed': 'सबसे उपयोगी',
    'control.chooseMode': 'मोड चुनें',
    'control.default': 'डिफ़ॉल्ट',
    'contrast.mode.light': 'लाइट कंट्रास्ट',
    'contrast.mode.dark': 'डार्क कंट्रास्ट',
    'contrast.mode.high': 'हाई कंट्रास्ट',
    'feature.contrast': 'कंट्रास्ट',
    'feature.textSize': 'बड़ा टेक्स्ट',
    'feature.keyboardNav': 'कीबोर्ड नेविगेशन',
    'feature.focusHighlight': 'फोकस हाइलाइट',
    'feature.linkHighlight': 'लिंक हाइलाइट',
    'feature.animationStop': 'एनिमेशन रोकें',
    'feature.readingGuide': 'रीडिंग गाइड',
    'feature.readingMask': 'रीडिंग मास्क',
    'feature.hideImages': 'तस्वीरें छिपाएँ',
    'feature.bigCursor': 'बड़ा कर्सर',
    'feature.pageStructure': 'पेज संरचना',
    'feature.tts': 'जोर से पढ़ें',
    'feature.textSimplify': 'टेक्स्ट सरल करें',
    'feature.altText': 'चित्र विवरण',
    'feature.autoScan': 'WCAG स्कैन',
    'section.profiles': 'त्वरित प्रोफ़ाइल',
    'profile.visionImpaired': 'दृष्टि बाधित',
    'profile.visionImpaired.desc': 'उच्च कंट्रास्ट, बड़ा टेक्स्ट और कर्सर',
    'profile.motorImpaired': 'मोटर बाधित',
    'profile.motorImpaired.desc': 'कीबोर्ड नेविगेशन और फोकस सहायता',
    'profile.cognitive': 'संज्ञानात्मक सहायता',
    'profile.cognitive.desc': 'पठन सहायता, स्पेसिंग और सरल फ़ॉन्ट',
    'profile.seizureSafe': 'दौरा सुरक्षित',
    'profile.seizureSafe.desc': 'एनिमेशन बंद करें और उत्तेजना कम करें',
    'profile.adhdFriendly': 'ADHD अनुकूल',
    'profile.adhdFriendly.desc': 'विकर्षण कम करें और ध्यान बढ़ाएँ',
  }),
  es: withEnglish({
    'widget.widgetLang': 'Idioma del widget',
    'widget.title': 'Accesibilidad',
    'widget.subtitle': 'Ajusta contraste, texto y navegacion en esta pagina',
    'widget.open': 'Abrir ajustes de accesibilidad',
    'widget.close': 'Cerrar',
    'widget.reset': 'Restablecer todo',
    'widget.resetConfirm': 'Se restablecieron todos los ajustes',
    'widget.poweredBy': 'Impulsado por Accessify-Widget',
    'widget.statement': 'Declaracion de accesibilidad',
    'widget.language': 'Idioma',
    'status.active': '{count} activas',
    'status.noActive': 'No hay ajustes activos',
    'status.activated': 'activado',
    'status.deactivated': 'desactivado',
    'section.essentials': 'Lo mas usado',
    'section.essentials.desc': 'Empieza con contraste, tamano de texto y apoyo para teclado.',
    'section.support': 'Lectura y navegacion',
    'section.support.desc': 'Herramientas para foco, menos movimiento y estructura de pagina.',
    'section.ai': 'Asistencia de IA',
    'section.ai.desc': 'Simplifica contenido, genera texto alternativo y analiza la pagina actual.',
    'control.mostUsed': 'Mas usado',
    'control.chooseMode': 'Elegir modo',
    'control.default': 'Predeterminado',
    'contrast.mode.light': 'Contraste claro',
    'contrast.mode.dark': 'Contraste oscuro',
    'contrast.mode.high': 'Alto contraste',
    'feature.contrast': 'Contraste',
    'feature.textSize': 'Texto mas grande',
    'feature.keyboardNav': 'Navegacion por teclado',
    'feature.focusHighlight': 'Resaltar foco',
    'feature.linkHighlight': 'Resaltar enlaces',
    'feature.animationStop': 'Detener animaciones',
    'feature.readingGuide': 'Guia de lectura',
    'feature.readingMask': 'Mascara de lectura',
    'feature.hideImages': 'Ocultar imagenes',
    'feature.bigCursor': 'Cursor grande',
    'feature.pageStructure': 'Estructura de pagina',
    'feature.tts': 'Lectura en voz alta',
    'feature.textSimplify': 'Simplificar texto',
    'feature.altText': 'Descripcion de imagen',
    'feature.autoScan': 'Escaneo WCAG',
    'section.profiles': 'Perfiles rápidos',
    'profile.visionImpaired': 'Discapacidad visual',
    'profile.visionImpaired.desc': 'Alto contraste, texto y cursor más grandes',
    'profile.motorImpaired': 'Discapacidad motora',
    'profile.motorImpaired.desc': 'Navegación por teclado y ayudas de enfoque',
    'profile.cognitive': 'Apoyo cognitivo',
    'profile.cognitive.desc': 'Ayudas de lectura, espaciado y fuentes más simples',
    'profile.seizureSafe': 'Seguro para convulsiones',
    'profile.seizureSafe.desc': 'Detener animaciones y reducir estímulos',
    'profile.adhdFriendly': 'Apto para TDAH',
    'profile.adhdFriendly.desc': 'Reducir distracciones y mejorar el enfoque',
  }),
  fr: withEnglish({
    'widget.widgetLang': 'Langue du widget',
    'widget.title': 'Accessibilite',
    'widget.subtitle': 'Ajustez le contraste, le texte et la navigation sur cette page',
    'widget.open': "Ouvrir les reglages d'accessibilite",
    'widget.close': 'Fermer',
    'widget.reset': 'Tout reinitialiser',
    'widget.resetConfirm': 'Tous les ajustements ont ete reinitialises',
    'widget.poweredBy': 'Propulse par Accessify-Widget',
    'widget.statement': "Declaration d'accessibilite",
    'widget.language': 'Langue',
    'status.active': '{count} actives',
    'status.noActive': 'Aucun ajustement actif',
    'status.activated': 'active',
    'status.deactivated': 'desactive',
    'section.essentials': 'Les plus utilises',
    'section.essentials.desc': 'Commencez par le contraste, la taille du texte et le clavier.',
    'section.support': 'Lecture et navigation',
    'section.support.desc': 'Outils pour la mise au point, moins de mouvement et la structure de page.',
    'section.ai': 'Assistance IA',
    'section.ai.desc': 'Simplifiez le contenu, generez un texte alternatif et analysez la page.',
    'control.mostUsed': 'Les plus utilises',
    'control.chooseMode': 'Choisir un mode',
    'control.default': 'Par defaut',
    'contrast.mode.light': 'Contraste clair',
    'contrast.mode.dark': 'Contraste sombre',
    'contrast.mode.high': 'Contraste eleve',
    'feature.contrast': 'Contraste',
    'feature.textSize': 'Texte plus grand',
    'feature.keyboardNav': 'Navigation clavier',
    'feature.focusHighlight': 'Mise en evidence du focus',
    'feature.linkHighlight': 'Mettre les liens en evidence',
    'feature.animationStop': 'Arreter les animations',
    'feature.readingGuide': 'Guide de lecture',
    'feature.readingMask': 'Masque de lecture',
    'feature.hideImages': 'Masquer les images',
    'feature.bigCursor': 'Grand curseur',
    'feature.pageStructure': 'Structure de page',
    'feature.tts': 'Lecture audio',
    'feature.textSimplify': 'Simplifier le texte',
    'feature.altText': "Description d'image",
    'feature.autoScan': 'Analyse WCAG',
    'section.profiles': 'Profils rapides',
    'profile.visionImpaired': 'Déficience visuelle',
    'profile.visionImpaired.desc': 'Contraste élevé, texte et curseur plus grands',
    'profile.motorImpaired': 'Déficience motrice',
    'profile.motorImpaired.desc': 'Navigation au clavier et aides à la mise au point',
    'profile.cognitive': 'Soutien cognitif',
    'profile.cognitive.desc': 'Aides à la lecture, espacement et polices simplifiées',
    'profile.seizureSafe': 'Protection contre les crises',
    'profile.seizureSafe.desc': 'Arrêter les animations et réduire les stimuli',
    'profile.adhdFriendly': 'Adapté au TDAH',
    'profile.adhdFriendly.desc': 'Réduire les distractions et améliorer la concentration',
  }),
  ar: withEnglish({
    'widget.widgetLang': 'لغة الأداة',
    'widget.title': 'إمكانية الوصول',
    'widget.subtitle': 'اضبط التباين والنص والتنقل في هذه الصفحة',
    'widget.open': 'افتح إعدادات إمكانية الوصول',
    'widget.close': 'إغلاق',
    'widget.reset': 'إعادة ضبط الكل',
    'widget.resetConfirm': 'تمت إعادة ضبط جميع التعديلات',
    'widget.poweredBy': 'مدعوم بواسطة Accessify-Widget',
    'widget.statement': 'بيان إمكانية الوصول',
    'widget.language': 'اللغة',
    'status.active': '{count} عناصر نشطة',
    'status.noActive': 'لا توجد تعديلات نشطة',
    'status.activated': 'مفعل',
    'status.deactivated': 'معطل',
    'section.essentials': 'الأكثر استخدامًا',
    'section.essentials.desc': 'ابدأ بالتباين وحجم النص ودعم لوحة المفاتيح.',
    'section.support': 'القراءة والتنقل',
    'section.support.desc': 'أدوات للتركيز وتقليل الحركة وبنية الصفحة.',
    'section.ai': 'مساعدة الذكاء الاصطناعي',
    'section.ai.desc': 'بسّط المحتوى وأنشئ نصًا بديلاً وافحص الصفحة الحالية.',
    'control.mostUsed': 'الأكثر استخدامًا',
    'control.chooseMode': 'اختر وضعًا',
    'control.default': 'افتراضي',
    'contrast.mode.light': 'تباين فاتح',
    'contrast.mode.dark': 'تباين داكن',
    'contrast.mode.high': 'تباين عالٍ',
    'feature.contrast': 'التباين',
    'feature.textSize': 'نص أكبر',
    'feature.keyboardNav': 'تنقل بلوحة المفاتيح',
    'feature.focusHighlight': 'إبراز التركيز',
    'feature.linkHighlight': 'إبراز الروابط',
    'feature.animationStop': 'إيقاف الرسوم المتحركة',
    'feature.readingGuide': 'دليل القراءة',
    'feature.readingMask': 'قناع القراءة',
    'feature.hideImages': 'إخفاء الصور',
    'feature.bigCursor': 'مؤشر كبير',
    'feature.pageStructure': 'بنية الصفحة',
    'feature.tts': 'القراءة بصوت عالٍ',
    'feature.textSimplify': 'تبسيط النص',
    'feature.altText': 'وصف الصورة',
    'feature.autoScan': 'فحص WCAG',
    'section.profiles': 'ملفات تعريف سريعة',
    'profile.visionImpaired': 'ضعف البصر',
    'profile.visionImpaired.desc': 'تباين عالٍ، نص ومؤشر أكبر',
    'profile.motorImpaired': 'إعاقة حركية',
    'profile.motorImpaired.desc': 'التنقل بلوحة المفاتيح ومساعدات التركيز',
    'profile.cognitive': 'دعم إدراكي',
    'profile.cognitive.desc': 'مساعدات القراءة والتباعد وخطوط أبسط',
    'profile.seizureSafe': 'آمن من النوبات',
    'profile.seizureSafe.desc': 'إيقاف الرسوم المتحركة وتقليل المحفزات',
    'profile.adhdFriendly': 'ملائم لاضطراب فرط الحركة',
    'profile.adhdFriendly.desc': 'تقليل المشتتات وتحسين التركيز',
  }),
  bn: withEnglish({
    'widget.title': 'অ্যাক্সেসিবিলিটি',
    'widget.subtitle': 'এই পাতায় কনট্রাস্ট, টেক্সট এবং নেভিগেশন ঠিক করুন',
    'widget.open': 'অ্যাক্সেসিবিলিটি সেটিংস খুলুন',
    'widget.close': 'বন্ধ করুন',
    'widget.reset': 'সব রিসেট করুন',
    'widget.resetConfirm': 'সব পরিবর্তন রিসেট হয়েছে',
    'widget.poweredBy': 'Accessify-Widget দ্বারা চালিত',
    'widget.statement': 'অ্যাক্সেসিবিলিটি বিবৃতি',
    'widget.language': 'ভাষা',
    'status.active': '{count} টি সক্রিয়',
    'status.noActive': 'কোনও সমন্বয় সক্রিয় নেই',
    'status.activated': 'সক্রিয়',
    'status.deactivated': 'নিষ্ক্রিয়',
    'section.essentials': 'সবচেয়ে ব্যবহৃত',
    'section.essentials.desc': 'কনট্রাস্ট, টেক্সট সাইজ এবং কিবোর্ড সহায়তা দিয়ে শুরু করুন।',
    'section.support': 'পড়া ও নেভিগেশন',
    'section.support.desc': 'ফোকাস, কম মুভমেন্ট এবং পেজ স্ট্রাকচারের টুল।',
    'section.ai': 'AI সহায়তা',
    'section.ai.desc': 'কনটেন্ট সহজ করুন, অল্ট টেক্সট তৈরি করুন এবং পেজ স্ক্যান করুন।',
    'control.mostUsed': 'সবচেয়ে ব্যবহৃত',
    'control.chooseMode': 'মোড বেছে নিন',
    'control.default': 'ডিফল্ট',
    'contrast.mode.light': 'লাইট কনট্রাস্ট',
    'contrast.mode.dark': 'ডার্ক কনট্রাস্ট',
    'contrast.mode.high': 'হাই কনট্রাস্ট',
    'feature.contrast': 'কনট্রাস্ট',
    'feature.textSize': 'বড় টেক্সট',
    'feature.keyboardNav': 'কিবোর্ড নেভিগেশন',
    'feature.focusHighlight': 'ফোকাস হাইলাইট',
    'feature.linkHighlight': 'লিংক হাইলাইট',
    'feature.animationStop': 'অ্যানিমেশন বন্ধ',
    'feature.readingGuide': 'রিডিং গাইড',
    'feature.readingMask': 'রিডিং মাস্ক',
    'feature.hideImages': 'ছবি লুকান',
    'feature.bigCursor': 'বড় কার্সর',
    'feature.pageStructure': 'পেজ স্ট্রাকচার',
    'feature.tts': 'জোরে পড়া',
    'feature.textSimplify': 'টেক্সট সহজ করুন',
    'feature.altText': 'ছবির বিবরণ',
    'feature.autoScan': 'WCAG স্ক্যান',
    'section.profiles': 'দ্রুত প্রোফাইল',
    'profile.visionImpaired': 'দৃষ্টি প্রতিবন্ধী',
    'profile.visionImpaired.desc': 'উচ্চ কনট্রাস্ট, বড় টেক্সট এবং কার্সর',
    'profile.motorImpaired': 'মোটর প্রতিবন্ধী',
    'profile.motorImpaired.desc': 'কীবোর্ড নেভিগেশন এবং ফোকাস সহায়তা',
    'profile.cognitive': 'জ্ঞানীয় সহায়তা',
    'profile.cognitive.desc': 'পড়ার সহায়তা, ব্যবধান এবং সরল ফন্ট',
    'profile.seizureSafe': 'খিঁচুনি নিরাপদ',
    'profile.seizureSafe.desc': 'অ্যানিমেশন বন্ধ করুন এবং উদ্দীপনা কমান',
    'profile.adhdFriendly': 'ADHD বান্ধব',
    'profile.adhdFriendly.desc': 'বিক্ষেপ কমান এবং মনোযোগ বাড়ান',
  }),
  pt: withEnglish({
    'widget.widgetLang': 'Idioma do widget',
    'widget.title': 'Acessibilidade',
    'widget.subtitle': 'Ajuste contraste, texto e navegacao nesta pagina',
    'widget.open': 'Abrir configuracoes de acessibilidade',
    'widget.close': 'Fechar',
    'widget.reset': 'Redefinir tudo',
    'widget.resetConfirm': 'Todos os ajustes foram redefinidos',
    'widget.poweredBy': 'Desenvolvido por Accessify-Widget',
    'widget.statement': 'Declaracao de acessibilidade',
    'widget.language': 'Idioma',
    'status.active': '{count} ativas',
    'status.noActive': 'Nenhum ajuste ativo',
    'status.activated': 'ativado',
    'status.deactivated': 'desativado',
    'section.essentials': 'Mais usados',
    'section.essentials.desc': 'Comece com contraste, tamanho do texto e apoio ao teclado.',
    'section.support': 'Leitura e navegacao',
    'section.support.desc': 'Ferramentas para foco, menos movimento e estrutura da pagina.',
    'section.ai': 'Assistencia de IA',
    'section.ai.desc': 'Simplifique conteudo, gere texto alternativo e analise a pagina atual.',
    'control.mostUsed': 'Mais usados',
    'control.chooseMode': 'Escolher modo',
    'control.default': 'Padrao',
    'contrast.mode.light': 'Contraste claro',
    'contrast.mode.dark': 'Contraste escuro',
    'contrast.mode.high': 'Alto contraste',
    'feature.contrast': 'Contraste',
    'feature.textSize': 'Texto maior',
    'feature.keyboardNav': 'Navegacao por teclado',
    'feature.focusHighlight': 'Destaque de foco',
    'feature.linkHighlight': 'Destacar links',
    'feature.animationStop': 'Parar animacoes',
    'feature.readingGuide': 'Guia de leitura',
    'feature.readingMask': 'Mascara de leitura',
    'feature.hideImages': 'Ocultar imagens',
    'feature.bigCursor': 'Cursor grande',
    'feature.pageStructure': 'Estrutura da pagina',
    'feature.tts': 'Leitura em voz alta',
    'feature.textSimplify': 'Simplificar texto',
    'feature.altText': 'Descricao da imagem',
    'feature.autoScan': 'Varredura WCAG',
    'section.profiles': 'Perfis rápidos',
    'profile.visionImpaired': 'Deficiência visual',
    'profile.visionImpaired.desc': 'Alto contraste, texto e cursor maiores',
    'profile.motorImpaired': 'Deficiência motora',
    'profile.motorImpaired.desc': 'Navegação por teclado e auxílios de foco',
    'profile.cognitive': 'Suporte cognitivo',
    'profile.cognitive.desc': 'Auxílios de leitura, espaçamento e fontes simplificadas',
    'profile.seizureSafe': 'Seguro para convulsões',
    'profile.seizureSafe.desc': 'Parar animações e reduzir estímulos',
    'profile.adhdFriendly': 'Adequado para TDAH',
    'profile.adhdFriendly.desc': 'Reduzir distrações e melhorar o foco',
  }),
  ru: withEnglish({
    'widget.widgetLang': 'Язык виджета',
    'widget.title': 'Доступность',
    'widget.subtitle': 'Настройте контраст, текст и навигацию на этой странице',
    'widget.open': 'Открыть настройки доступности',
    'widget.close': 'Закрыть',
    'widget.reset': 'Сбросить все',
    'widget.resetConfirm': 'Все настройки сброшены',
    'widget.poweredBy': 'Работает на Accessify-Widget',
    'widget.statement': 'Заявление о доступности',
    'widget.language': 'Язык',
    'status.active': 'Активно: {count}',
    'status.noActive': 'Нет активных настроек',
    'status.activated': 'включено',
    'status.deactivated': 'выключено',
    'section.essentials': 'Самое используемое',
    'section.essentials.desc': 'Начните с контраста, размера текста и поддержки клавиатуры.',
    'section.support': 'Чтение и навигация',
    'section.support.desc': 'Инструменты для фокуса, уменьшения движения и структуры страницы.',
    'section.ai': 'Помощь ИИ',
    'section.ai.desc': 'Упрощайте текст, создавайте alt-текст и проверяйте текущую страницу.',
    'control.mostUsed': 'Популярное',
    'control.chooseMode': 'Выберите режим',
    'control.default': 'По умолчанию',
    'contrast.mode.light': 'Светлый контраст',
    'contrast.mode.dark': 'Темный контраст',
    'contrast.mode.high': 'Высокий контраст',
    'feature.contrast': 'Контраст',
    'feature.textSize': 'Крупный текст',
    'feature.keyboardNav': 'Навигация с клавиатуры',
    'feature.focusHighlight': 'Подсветка фокуса',
    'feature.linkHighlight': 'Выделить ссылки',
    'feature.animationStop': 'Остановить анимацию',
    'feature.readingGuide': 'Линейка для чтения',
    'feature.readingMask': 'Маска чтения',
    'feature.hideImages': 'Скрыть изображения',
    'feature.bigCursor': 'Большой курсор',
    'feature.pageStructure': 'Структура страницы',
    'feature.tts': 'Озвучивание',
    'feature.textSimplify': 'Упростить текст',
    'feature.altText': 'Описание изображения',
    'feature.autoScan': 'Проверка WCAG',
    'section.profiles': 'Быстрые профили',
    'profile.visionImpaired': 'Нарушение зрения',
    'profile.visionImpaired.desc': 'Высокий контраст, увеличенный текст и курсор',
    'profile.motorImpaired': 'Нарушение моторики',
    'profile.motorImpaired.desc': 'Навигация с клавиатуры и помощь с фокусом',
    'profile.cognitive': 'Когнитивная поддержка',
    'profile.cognitive.desc': 'Помощь при чтении, интервалы и упрощённые шрифты',
    'profile.seizureSafe': 'Защита от припадков',
    'profile.seizureSafe.desc': 'Остановить анимации и уменьшить раздражители',
    'profile.adhdFriendly': 'Подходит при СДВГ',
    'profile.adhdFriendly.desc': 'Уменьшить отвлечения и улучшить концентрацию',
  }),
  ur: withEnglish({
    'widget.title': 'رسائی',
    'widget.subtitle': 'اس صفحے پر کنٹراسٹ، متن اور نیویگیشن کو ایڈجسٹ کریں',
    'widget.open': 'رسائی کی ترتیبات کھولیں',
    'widget.close': 'بند کریں',
    'widget.reset': 'سب ری سیٹ کریں',
    'widget.resetConfirm': 'تمام تبدیلیاں ری سیٹ ہو گئیں',
    'widget.poweredBy': 'Accessify-Widget کے ذریعے',
    'widget.statement': 'رسائی کا بیان',
    'widget.language': 'زبان',
    'status.active': '{count} فعال',
    'status.noActive': 'کوئی تبدیلی فعال نہیں',
    'status.activated': 'فعال',
    'status.deactivated': 'غیر فعال',
    'section.essentials': 'سب سے زیادہ استعمال',
    'section.essentials.desc': 'کنٹراسٹ، متن کے سائز اور کی بورڈ مدد سے شروع کریں۔',
    'section.support': 'مطالعہ اور نیویگیشن',
    'section.support.desc': 'فوکس، کم حرکت اور صفحہ ساخت کے لیے ٹولز۔',
    'section.ai': 'AI مدد',
    'section.ai.desc': 'مواد آسان کریں، آلٹ ٹیکسٹ بنائیں اور موجودہ صفحہ اسکین کریں۔',
    'control.mostUsed': 'زیادہ استعمال',
    'control.chooseMode': 'موڈ منتخب کریں',
    'control.default': 'ڈیفالٹ',
    'contrast.mode.light': 'لائٹ کنٹراسٹ',
    'contrast.mode.dark': 'ڈارک کنٹراسٹ',
    'contrast.mode.high': 'ہائی کنٹراسٹ',
    'feature.contrast': 'کنٹراسٹ',
    'feature.textSize': 'بڑا متن',
    'feature.keyboardNav': 'کی بورڈ نیویگیشن',
    'feature.focusHighlight': 'فوکس ہائی لائٹ',
    'feature.linkHighlight': 'لنکس نمایاں کریں',
    'feature.animationStop': 'اینیمیشن روکیں',
    'feature.readingGuide': 'مطالعہ گائیڈ',
    'feature.readingMask': 'مطالعہ ماسک',
    'feature.hideImages': 'تصاویر چھپائیں',
    'feature.bigCursor': 'بڑا کرسر',
    'feature.pageStructure': 'صفحہ ساخت',
    'feature.tts': 'بلند آواز میں پڑھیں',
    'feature.textSimplify': 'متن آسان کریں',
    'feature.altText': 'تصویر کی تفصیل',
    'feature.autoScan': 'WCAG اسکین',
    'section.profiles': 'فوری پروفائلز',
    'profile.visionImpaired': 'بصارت سے محروم',
    'profile.visionImpaired.desc': 'اعلیٰ کنٹراسٹ، بڑا متن اور کرسر',
    'profile.motorImpaired': 'حرکی معذوری',
    'profile.motorImpaired.desc': 'کی بورڈ نیویگیشن اور فوکس معاونت',
    'profile.cognitive': 'ادراکی معاونت',
    'profile.cognitive.desc': 'پڑھنے میں مدد، فاصلہ اور آسان فونٹس',
    'profile.seizureSafe': 'دورے سے محفوظ',
    'profile.seizureSafe.desc': 'اینیمیشنز بند کریں اور محرکات کم کریں',
    'profile.adhdFriendly': 'ADHD کے لیے موزوں',
    'profile.adhdFriendly.desc': 'خلفشار کم کریں اور توجہ بہتر بنائیں',
  }),
  id: withEnglish({
    'widget.title': 'Aksesibilitas',
    'widget.subtitle': 'Atur kontras, teks, dan navigasi di halaman ini',
    'widget.open': 'Buka pengaturan aksesibilitas',
    'widget.close': 'Tutup',
    'widget.reset': 'Reset semua',
    'widget.resetConfirm': 'Semua penyesuaian telah direset',
    'widget.poweredBy': 'Didukung oleh Accessify-Widget',
    'widget.statement': 'Pernyataan aksesibilitas',
    'widget.language': 'Bahasa',
    'status.active': '{count} aktif',
    'status.noActive': 'Tidak ada penyesuaian aktif',
    'status.activated': 'aktif',
    'status.deactivated': 'nonaktif',
    'section.essentials': 'Paling sering dipakai',
    'section.essentials.desc': 'Mulai dari kontras, ukuran teks, dan dukungan keyboard.',
    'section.support': 'Membaca dan navigasi',
    'section.support.desc': 'Alat untuk fokus, gerakan rendah, dan struktur halaman.',
    'section.ai': 'Bantuan AI',
    'section.ai.desc': 'Sederhanakan konten, buat alt text, dan pindai halaman saat ini.',
    'control.mostUsed': 'Paling sering',
    'control.chooseMode': 'Pilih mode',
    'control.default': 'Default',
    'contrast.mode.light': 'Kontras terang',
    'contrast.mode.dark': 'Kontras gelap',
    'contrast.mode.high': 'Kontras tinggi',
    'feature.contrast': 'Kontras',
    'feature.textSize': 'Teks lebih besar',
    'feature.keyboardNav': 'Navigasi keyboard',
    'feature.focusHighlight': 'Sorot fokus',
    'feature.linkHighlight': 'Sorot tautan',
    'feature.animationStop': 'Hentikan animasi',
    'feature.readingGuide': 'Panduan baca',
    'feature.readingMask': 'Masker baca',
    'feature.hideImages': 'Sembunyikan gambar',
    'feature.bigCursor': 'Kursor besar',
    'feature.pageStructure': 'Struktur halaman',
    'feature.tts': 'Bacakan',
    'feature.textSimplify': 'Sederhanakan teks',
    'feature.altText': 'Deskripsi gambar',
    'feature.autoScan': 'Pindai WCAG',
    'section.profiles': 'Profil Cepat',
    'profile.visionImpaired': 'Gangguan Penglihatan',
    'profile.visionImpaired.desc': 'Kontras tinggi, teks dan kursor lebih besar',
    'profile.motorImpaired': 'Gangguan Motorik',
    'profile.motorImpaired.desc': 'Navigasi keyboard dan bantuan fokus',
    'profile.cognitive': 'Dukungan Kognitif',
    'profile.cognitive.desc': 'Bantuan membaca, spasi, dan font yang lebih sederhana',
    'profile.seizureSafe': 'Aman dari Kejang',
    'profile.seizureSafe.desc': 'Hentikan animasi dan kurangi rangsangan',
    'profile.adhdFriendly': 'Ramah ADHD',
    'profile.adhdFriendly.desc': 'Kurangi gangguan dan tingkatkan fokus',
  }),
  ja: withEnglish({
    'widget.widgetLang': 'ウィジェットの言語',
    'widget.title': 'アクセシビリティ',
    'widget.subtitle': 'このページのコントラスト、文字、ナビゲーションを調整します',
    'widget.open': 'アクセシビリティ設定を開く',
    'widget.close': '閉じる',
    'widget.reset': 'すべてリセット',
    'widget.resetConfirm': 'すべての調整をリセットしました',
    'widget.poweredBy': 'Accessify-Widget 提供',
    'widget.statement': 'アクセシビリティ宣言',
    'widget.language': '言語',
    'status.active': '{count} 件が有効',
    'status.noActive': '有効な調整はありません',
    'status.activated': '有効',
    'status.deactivated': '無効',
    'section.essentials': 'よく使う設定',
    'section.essentials.desc': 'まずはコントラスト、文字サイズ、キーボード支援から始めてください。',
    'section.support': '読みやすさとナビゲーション',
    'section.support.desc': 'フォーカス、動きの抑制、ページ構造のためのツールです。',
    'section.ai': 'AI 支援',
    'section.ai.desc': '文章を簡単にし、代替テキストを生成し、現在のページを確認します。',
    'control.mostUsed': 'よく使う',
    'control.chooseMode': 'モードを選択',
    'control.default': '標準',
    'contrast.mode.light': 'ライトコントラスト',
    'contrast.mode.dark': 'ダークコントラスト',
    'contrast.mode.high': 'ハイコントラスト',
    'feature.contrast': 'コントラスト',
    'feature.textSize': '大きな文字',
    'feature.keyboardNav': 'キーボード操作',
    'feature.focusHighlight': 'フォーカス強調',
    'feature.linkHighlight': 'リンク強調',
    'feature.animationStop': 'アニメーション停止',
    'feature.readingGuide': '読書ガイド',
    'feature.readingMask': '読書マスク',
    'feature.hideImages': '画像を隠す',
    'feature.bigCursor': '大きいカーソル',
    'feature.pageStructure': 'ページ構造',
    'feature.tts': '読み上げ',
    'feature.textSimplify': '文章を簡単にする',
    'feature.altText': '画像の説明',
    'feature.autoScan': 'WCAG スキャン',
    'section.profiles': 'クイックプロフィール',
    'profile.visionImpaired': '視覚障害',
    'profile.visionImpaired.desc': '高コントラスト、大きな文字とカーソル',
    'profile.motorImpaired': '運動障害',
    'profile.motorImpaired.desc': 'キーボードナビゲーションとフォーカス補助',
    'profile.cognitive': '認知サポート',
    'profile.cognitive.desc': '読書支援、間隔調整、シンプルなフォント',
    'profile.seizureSafe': '発作防止',
    'profile.seizureSafe.desc': 'アニメーションを停止し刺激を軽減',
    'profile.adhdFriendly': 'ADHD対応',
    'profile.adhdFriendly.desc': '気が散る要素を減らし集中力を向上',
  }),
  pcm: withEnglish({
    'widget.title': 'Accessibility',
    'widget.subtitle': 'Adjust contrast, text and navigation for dis page',
    'widget.open': 'Open accessibility settings',
    'widget.close': 'Close',
    'widget.reset': 'Reset all',
    'widget.resetConfirm': 'All adjustments don reset',
    'widget.poweredBy': 'Powered by Accessify-Widget',
    'widget.statement': 'Accessibility statement',
    'widget.language': 'Language',
    'status.active': '{count} active',
    'status.noActive': 'No adjustment dey active',
    'status.activated': 'activated',
    'status.deactivated': 'deactivated',
    'section.essentials': 'Wetin people use pass',
    'section.essentials.desc': 'Start with contrast, text size and keyboard support.',
    'section.support': 'Reading and navigation',
    'section.support.desc': 'Tools for focus, reduced motion and page structure.',
    'section.ai': 'AI help',
    'section.ai.desc': 'Simplify content, generate alt text and scan dis page.',
    'control.mostUsed': 'Most used',
    'control.chooseMode': 'Choose mode',
    'control.default': 'Default',
    'contrast.mode.light': 'Light contrast',
    'contrast.mode.dark': 'Dark contrast',
    'contrast.mode.high': 'High contrast',
    'feature.contrast': 'Contrast',
    'feature.textSize': 'Bigger text',
    'feature.keyboardNav': 'Keyboard navigation',
    'feature.focusHighlight': 'Focus highlight',
    'feature.linkHighlight': 'Highlight links',
    'feature.animationStop': 'Stop animations',
    'feature.readingGuide': 'Reading guide',
    'feature.readingMask': 'Reading mask',
    'feature.hideImages': 'Hide images',
    'feature.bigCursor': 'Big cursor',
    'feature.pageStructure': 'Page structure',
    'feature.tts': 'Read aloud',
    'feature.textSimplify': 'Simplify text',
    'feature.altText': 'Image Description',
    'feature.autoScan': 'WCAG scan',
    'section.profiles': 'Quick Profiles',
    'profile.visionImpaired': 'Eye No Dey See Well',
    'profile.visionImpaired.desc': 'High contrast, big text and cursor',
    'profile.motorImpaired': 'Body Movement Wahala',
    'profile.motorImpaired.desc': 'Keyboard navigation and focus help',
    'profile.cognitive': 'Brain Support',
    'profile.cognitive.desc': 'Reading help, spacing, and simple fonts',
    'profile.seizureSafe': 'Seizure Safe',
    'profile.seizureSafe.desc': 'Stop animation and reduce wahala for eye',
    'profile.adhdFriendly': 'ADHD Friendly',
    'profile.adhdFriendly.desc': 'Reduce distraction and help you focus well',
  }),
  mr: withEnglish({
    'widget.title': 'प्रवेशयोग्यता',
    'widget.subtitle': 'या पानावर कॉन्ट्रास्ट, मजकूर आणि नेव्हिगेशन समायोजित करा',
    'widget.open': 'प्रवेशयोग्यता सेटिंग्ज उघडा',
    'widget.close': 'बंद करा',
    'widget.reset': 'सर्व रीसेट करा',
    'widget.resetConfirm': 'सर्व बदल रीसेट झाले',
    'widget.poweredBy': 'Accessify-Widget द्वारे समर्थित',
    'widget.statement': 'प्रवेशयोग्यता निवेदन',
    'widget.language': 'भाषा',
    'status.active': '{count} सक्रिय',
    'status.noActive': 'कोणतेही बदल सक्रिय नाहीत',
    'status.activated': 'सक्रिय',
    'status.deactivated': 'निष्क्रिय',
    'section.essentials': 'सर्वात जास्त वापरलेले',
    'section.essentials.desc': 'कॉन्ट्रास्ट, मजकूर आकार आणि कीबोर्ड सहाय्यापासून सुरुवात करा.',
    'section.support': 'वाचन आणि नेव्हिगेशन',
    'section.support.desc': 'फोकस, कमी हालचाल आणि पृष्ठरचना यासाठी साधने.',
    'section.ai': 'AI मदत',
    'section.ai.desc': 'मजकूर सोपा करा, alt-text तयार करा आणि हे पान स्कॅन करा.',
    'control.mostUsed': 'सर्वाधिक वापरलेले',
    'control.chooseMode': 'मोड निवडा',
    'control.default': 'डीफॉल्ट',
    'contrast.mode.light': 'लाईट कॉन्ट्रास्ट',
    'contrast.mode.dark': 'डार्क कॉन्ट्रास्ट',
    'contrast.mode.high': 'हाय कॉन्ट्रास्ट',
    'feature.contrast': 'कॉन्ट्रास्ट',
    'feature.textSize': 'मोठा मजकूर',
    'feature.keyboardNav': 'कीबोर्ड नेव्हिगेशन',
    'feature.focusHighlight': 'फोकस हायलाइट',
    'feature.linkHighlight': 'लिंक हायलाइट',
    'feature.animationStop': 'अ‍ॅनिमेशन थांबवा',
    'feature.readingGuide': 'वाचन मार्गदर्शक',
    'feature.readingMask': 'वाचन मास्क',
    'feature.hideImages': 'प्रतिमा लपवा',
    'feature.bigCursor': 'मोठा कर्सर',
    'feature.pageStructure': 'पृष्ठरचना',
    'feature.tts': 'मोठ्याने वाचा',
    'feature.textSimplify': 'मजकूर सोपा करा',
    'feature.altText': 'चित्र वर्णन',
    'feature.autoScan': 'WCAG स्कॅन',
    'section.profiles': 'जलद प्रोफाइल',
    'profile.visionImpaired': 'दृष्टिदोष',
    'profile.visionImpaired.desc': 'उच्च कॉन्ट्रास्ट, मोठा मजकूर आणि कर्सर',
    'profile.motorImpaired': 'मोटर अक्षमता',
    'profile.motorImpaired.desc': 'कीबोर्ड नेव्हिगेशन आणि फोकस सहाय्य',
    'profile.cognitive': 'संज्ञानात्मक सहाय्य',
    'profile.cognitive.desc': 'वाचन सहाय्य, अंतर आणि सोपे फॉन्ट',
    'profile.seizureSafe': 'झटके सुरक्षित',
    'profile.seizureSafe.desc': 'अॅनिमेशन थांबवा आणि उत्तेजना कमी करा',
    'profile.adhdFriendly': 'ADHD अनुकूल',
    'profile.adhdFriendly.desc': 'विचलन कमी करा आणि लक्ष सुधारा',
  }),
  te: withEnglish({
    'widget.title': 'అందుబాటు',
    'widget.subtitle': 'ఈ పేజీలో కాంట్రాస్ట్, టెక్స్ట్ మరియు నావిగేషన్‌ను సర్దుబాటు చేయండి',
    'widget.open': 'అందుబాటు సెట్టింగ్స్ తెరువు',
    'widget.close': 'మూసివేయి',
    'widget.reset': 'అన్నీ రీసెట్ చేయి',
    'widget.resetConfirm': 'అన్ని మార్పులు రీసెట్ అయ్యాయి',
    'widget.poweredBy': 'Accessify-Widget ద్వారా',
    'widget.statement': 'అందుబాటు ప్రకటన',
    'widget.language': 'భాష',
    'status.active': '{count} సక్రియం',
    'status.noActive': 'ఏ మార్పూ సక్రియంగా లేదు',
    'status.activated': 'సక్రియం',
    'status.deactivated': 'నిష్క్రియం',
    'section.essentials': 'ఎక్కువగా ఉపయోగించేవి',
    'section.essentials.desc': 'కాంట్రాస్ట్, టెక్స్ట్ సైజు మరియు కీబోర్డ్ సహాయంతో మొదలుపెట్టండి.',
    'section.support': 'చదువు మరియు నావిగేషన్',
    'section.support.desc': 'ఫోకస్, తక్కువ మోషన్ మరియు పేజీ నిర్మాణానికి టూల్స్.',
    'section.ai': 'AI సహాయం',
    'section.ai.desc': 'కంటెంట్‌ను సులభతరం చేయండి, alt-text తయారు చేయండి, ఈ పేజీని స్కాన్ చేయండి.',
    'control.mostUsed': 'ఎక్కువ ఉపయోగం',
    'control.chooseMode': 'మోడ్ ఎంచుకోండి',
    'control.default': 'డిఫాల్ట్',
    'contrast.mode.light': 'లైట్ కాంట్రాస్ట్',
    'contrast.mode.dark': 'డార్క్ కాంట్రాస్ట్',
    'contrast.mode.high': 'హై కాంట్రాస్ట్',
    'feature.contrast': 'కాంట్రాస్ట్',
    'feature.textSize': 'పెద్ద టెక్స్ట్',
    'feature.keyboardNav': 'కీబోర్డ్ నావిగేషన్',
    'feature.focusHighlight': 'ఫోకస్ హైలైట్',
    'feature.linkHighlight': 'లింక్ హైలైట్',
    'feature.animationStop': 'యానిమేషన్స్ ఆపండి',
    'feature.readingGuide': 'రీడింగ్ గైడ్',
    'feature.readingMask': 'రీడింగ్ మాస్క్',
    'feature.hideImages': 'చిత్రాలు దాచు',
    'feature.bigCursor': 'పెద్ద కర్సర్',
    'feature.pageStructure': 'పేజీ నిర్మాణం',
    'feature.tts': 'గట్టిగా చదవండి',
    'feature.textSimplify': 'టెక్స్ట్ సులభతరం చేయి',
    'feature.altText': 'చిత్ర వివరణ',
    'feature.autoScan': 'WCAG స్కాన్',
    'section.profiles': 'త్వరిత ప్రొఫైల్‌లు',
    'profile.visionImpaired': 'దృష్టి లోపం',
    'profile.visionImpaired.desc': 'అధిక కాంట్రాస్ట్, పెద్ద టెక్స్ట్ మరియు కర్సర్',
    'profile.motorImpaired': 'మోటార్ లోపం',
    'profile.motorImpaired.desc': 'కీబోర్డ్ నావిగేషన్ మరియు ఫోకస్ సహాయాలు',
    'profile.cognitive': 'అభిజ్ఞా మద్దతు',
    'profile.cognitive.desc': 'చదవడంలో సహాయం, అంతరాలు మరియు సరళ ఫాంట్‌లు',
    'profile.seizureSafe': 'మూర్ఛ భద్రత',
    'profile.seizureSafe.desc': 'యానిమేషన్‌లను ఆపండి మరియు ఉద్దీపనలను తగ్గించండి',
    'profile.adhdFriendly': 'ADHD అనుకూలం',
    'profile.adhdFriendly.desc': 'పరధ్యానాన్ని తగ్గించండి మరియు దృష్టిని మెరుగుపరచండి',
  }),
  tr: withEnglish({
    'widget.widgetLang': 'Widget dili',
    'widget.title': 'Erisilebilirlik',
    'widget.subtitle': 'Bu sayfada kontrast, metin ve gezinmeyi ayarlayin',
    'widget.open': 'Erisilebilirlik ayarlarini ac',
    'widget.close': 'Kapat',
    'widget.reset': 'Tumunu sifirla',
    'widget.resetConfirm': 'Tum ayarlar sifirlandi',
    'widget.poweredBy': 'Accessify-Widget ile calisir',
    'widget.statement': 'Erisilebilirlik bildirimi',
    'widget.language': 'Dil',
    'status.active': '{count} etkin',
    'status.noActive': 'Etkin ayar yok',
    'status.activated': 'etkin',
    'status.deactivated': 'devre disi',
    'section.essentials': 'En cok kullanilanlar',
    'section.essentials.desc': 'Kontrast, yazi boyutu ve klavye destegi ile baslayin.',
    'section.support': 'Okuma ve gezinme',
    'section.support.desc': 'Odak, azaltulmis hareket ve sayfa yapisi icin araclar.',
    'section.ai': 'YZ yardimi',
    'section.ai.desc': 'Icerigi sadeletir, alt metin olusturur ve mevcut sayfayi tarar.',
    'control.mostUsed': 'En cok kullanilan',
    'control.chooseMode': 'Mod sec',
    'control.default': 'Varsayilan',
    'contrast.mode.light': 'Acik kontrast',
    'contrast.mode.dark': 'Koyu kontrast',
    'contrast.mode.high': 'Yuksek kontrast',
    'feature.contrast': 'Kontrast',
    'feature.textSize': 'Buyuk metin',
    'feature.keyboardNav': 'Klavye gezintisi',
    'feature.focusHighlight': 'Odak vurgusu',
    'feature.linkHighlight': 'Baglantilari vurgula',
    'feature.animationStop': 'Animasyonlari durdur',
    'feature.readingGuide': 'Okuma kilavuzu',
    'feature.readingMask': 'Okuma maskesi',
    'feature.hideImages': 'Gorselleri gizle',
    'feature.bigCursor': 'Buyuk imlec',
    'feature.pageStructure': 'Sayfa yapisi',
    'feature.tts': 'Sesli oku',
    'feature.textSimplify': 'Metni sadeletir',
    'feature.altText': 'Resim aciklamasi',
    'feature.autoScan': 'WCAG taramasi',
    'section.profiles': 'Hizli Profiller',
    'profile.visionImpaired': 'Gorme Engelli',
    'profile.visionImpaired.desc': 'Yuksek kontrast, buyuk yazi ve imlec',
    'profile.motorImpaired': 'Motor Engelli',
    'profile.motorImpaired.desc': 'Klavye gezinme ve odaklanma yardimlari',
    'profile.cognitive': 'Bilissel Destek',
    'profile.cognitive.desc': 'Okuma yardimlari, bosluk ve basit yazitipleri',
    'profile.seizureSafe': 'Nobetlere Karsi Guvenli',
    'profile.seizureSafe.desc': 'Animasyonlari durdurun ve uyaranlari azaltin',
    'profile.adhdFriendly': 'DEHB Dostu',
    'profile.adhdFriendly.desc': 'Dikkat dagitici unsurlari azaltin ve odaklanmayi artirin',
  }),
  ta: withEnglish({
    'widget.title': 'அணுகல்திறன்',
    'widget.subtitle': 'இந்த பக்கத்தில் எதிரொலி, உரை மற்றும் வழிசெலுத்தலை மாற்றுங்கள்',
    'widget.open': 'அணுகல்திறன் அமைப்புகளை திறக்கவும்',
    'widget.close': 'மூடு',
    'widget.reset': 'அனைத்தையும் மீட்டமை',
    'widget.resetConfirm': 'அனைத்து மாற்றங்களும் மீட்டமைக்கப்பட்டன',
    'widget.poweredBy': 'Accessify-Widget மூலம்',
    'widget.statement': 'அணுகல்திறன் அறிக்கை',
    'widget.language': 'மொழி',
    'status.active': '{count} செயலில்',
    'status.noActive': 'செயலில் மாற்றங்கள் இல்லை',
    'status.activated': 'செயலில்',
    'status.deactivated': 'செயலற்றது',
    'section.essentials': 'அதிகம் பயன்படுத்தப்படும்',
    'section.essentials.desc': 'எதிரொலி, எழுத்து அளவு மற்றும் விசைப்பலகை உதவியுடன் தொடங்குங்கள்.',
    'section.support': 'வாசிப்பு மற்றும் வழிசெலுத்தல்',
    'section.support.desc': 'கவனம், குறைந்த இயக்கம் மற்றும் பக்க அமைப்புக்கான கருவிகள்.',
    'section.ai': 'AI உதவி',
    'section.ai.desc': 'உள்ளடக்கத்தை எளிமைப்படுத்தி, alt-text உருவாக்கி, இந்த பக்கத்தை ஸ்கேன் செய்யுங்கள்.',
    'control.mostUsed': 'அதிகம் பயன்படுத்தப்படும்',
    'control.chooseMode': 'முறையை தேர்வு செய்',
    'control.default': 'இயல்புநிலை',
    'contrast.mode.light': 'ஒளி எதிரொலி',
    'contrast.mode.dark': 'இருண்ட எதிரொலி',
    'contrast.mode.high': 'உயர் எதிரொலி',
    'feature.contrast': 'எதிரொலி',
    'feature.textSize': 'பெரிய எழுத்து',
    'feature.keyboardNav': 'விசைப்பலகை வழிசெலுத்தல்',
    'feature.focusHighlight': 'கவனத்தை வெளிப்படுத்து',
    'feature.linkHighlight': 'இணைப்புகளை வெளிப்படுத்து',
    'feature.animationStop': 'அனிமேஷனை நிறுத்து',
    'feature.readingGuide': 'வாசிப்பு வழிகாட்டி',
    'feature.readingMask': 'வாசிப்பு மறைப்பு',
    'feature.hideImages': 'படங்களை மறை',
    'feature.bigCursor': 'பெரிய கர்சர்',
    'feature.pageStructure': 'பக்க அமைப்பு',
    'feature.tts': 'சத்தமாக வாசி',
    'feature.textSimplify': 'உரையை எளிமைப்படுத்து',
    'feature.altText': 'பட விளக்கம்',
    'feature.autoScan': 'WCAG ஸ்கேன்',
    'section.profiles': 'விரைவு சுயவிவரங்கள்',
    'profile.visionImpaired': 'பார்வை குறைபாடு',
    'profile.visionImpaired.desc': 'அதிக மாறுபாடு, பெரிய உரை மற்றும் கர்சர்',
    'profile.motorImpaired': 'இயக்க குறைபாடு',
    'profile.motorImpaired.desc': 'விசைப்பலகை வழிசெலுத்தல் மற்றும் கவன உதவிகள்',
    'profile.cognitive': 'அறிவாற்றல் ஆதரவு',
    'profile.cognitive.desc': 'வாசிப்பு உதவிகள், இடைவெளி மற்றும் எளிய எழுத்துருக்கள்',
    'profile.seizureSafe': 'வலிப்பு பாதுகாப்பு',
    'profile.seizureSafe.desc': 'அனிமேஷன்களை நிறுத்தி தூண்டுதல்களைக் குறைக்கவும்',
    'profile.adhdFriendly': 'ADHD நட்பு',
    'profile.adhdFriendly.desc': 'கவனச்சிதறல்களைக் குறைத்து கவனத்தை மேம்படுத்தவும்',
  }),
  vi: withEnglish({
    'widget.title': 'Tro nang truy cap',
    'widget.subtitle': 'Dieu chinh tuong phan, van ban va dieu huong tren trang nay',
    'widget.open': 'Mo cai dat tro nang truy cap',
    'widget.close': 'Dong',
    'widget.reset': 'Dat lai tat ca',
    'widget.resetConfirm': 'Da dat lai moi dieu chinh',
    'widget.poweredBy': 'Van hanh boi Accessify-Widget',
    'widget.statement': 'Tuyen bo tro nang truy cap',
    'widget.language': 'Ngon ngu',
    'status.active': '{count} muc dang bat',
    'status.noActive': 'Khong co dieu chinh nao dang bat',
    'status.activated': 'da bat',
    'status.deactivated': 'da tat',
    'section.essentials': 'Dung nhieu nhat',
    'section.essentials.desc': 'Bat dau voi tuong phan, co chu va ho tro ban phim.',
    'section.support': 'Doc va dieu huong',
    'section.support.desc': 'Cong cu cho tap trung, giam chuyen dong va cau truc trang.',
    'section.ai': 'Ho tro AI',
    'section.ai.desc': 'Don gian hoa noi dung, tao alt-text va quet trang hien tai.',
    'control.mostUsed': 'Pho bien',
    'control.chooseMode': 'Chon che do',
    'control.default': 'Mac dinh',
    'contrast.mode.light': 'Tuong phan sang',
    'contrast.mode.dark': 'Tuong phan toi',
    'contrast.mode.high': 'Tuong phan cao',
    'feature.contrast': 'Tuong phan',
    'feature.textSize': 'Chu lon hon',
    'feature.keyboardNav': 'Dieu huong ban phim',
    'feature.focusHighlight': 'Noi bat tieu diem',
    'feature.linkHighlight': 'Noi bat lien ket',
    'feature.animationStop': 'Dung hieu ung',
    'feature.readingGuide': 'Thuoc doc',
    'feature.readingMask': 'Mat na doc',
    'feature.hideImages': 'An hinh anh',
    'feature.bigCursor': 'Con tro lon',
    'feature.pageStructure': 'Cau truc trang',
    'feature.tts': 'Doc thanh tieng',
    'feature.textSimplify': 'Don gian hoa van ban',
    'feature.altText': 'Mo ta hinh anh',
    'feature.autoScan': 'Quet WCAG',
    'section.profiles': 'Hồ sơ nhanh',
    'profile.visionImpaired': 'Khiếm thị',
    'profile.visionImpaired.desc': 'Tương phản cao, chữ và con trỏ lớn hơn',
    'profile.motorImpaired': 'Khuyết tật vận động',
    'profile.motorImpaired.desc': 'Điều hướng bàn phím và hỗ trợ tiêu điểm',
    'profile.cognitive': 'Hỗ trợ nhận thức',
    'profile.cognitive.desc': 'Hỗ trợ đọc, giãn cách và phông chữ đơn giản',
    'profile.seizureSafe': 'An toàn co giật',
    'profile.seizureSafe.desc': 'Dừng hoạt ảnh và giảm kích thích',
    'profile.adhdFriendly': 'Thân thiện ADHD',
    'profile.adhdFriendly.desc': 'Giảm phiền nhiễu và cải thiện sự tập trung',
  }),
  ko: withEnglish({
    'widget.widgetLang': '위젯 언어',
    'widget.title': '접근성',
    'widget.subtitle': '이 페이지의 대비, 텍스트, 내비게이션을 조정하세요',
    'widget.open': '접근성 설정 열기',
    'widget.close': '닫기',
    'widget.reset': '모두 재설정',
    'widget.resetConfirm': '모든 조정이 재설정되었습니다',
    'widget.poweredBy': 'Accessify-Widget 제공',
    'widget.statement': '접근성 안내문',
    'widget.language': '언어',
    'status.active': '{count}개 활성',
    'status.noActive': '활성화된 조정이 없습니다',
    'status.activated': '활성화됨',
    'status.deactivated': '비활성화됨',
    'section.essentials': '가장 많이 사용됨',
    'section.essentials.desc': '대비, 글자 크기, 키보드 지원부터 시작하세요.',
    'section.support': '읽기 및 내비게이션',
    'section.support.desc': '초점, 움직임 감소, 페이지 구조를 위한 도구입니다.',
    'section.ai': 'AI 지원',
    'section.ai.desc': '콘텐츠를 쉽게 만들고 대체 텍스트를 생성하며 현재 페이지를 검사합니다.',
    'control.mostUsed': '가장 많이 사용됨',
    'control.chooseMode': '모드 선택',
    'control.default': '기본값',
    'contrast.mode.light': '라이트 대비',
    'contrast.mode.dark': '다크 대비',
    'contrast.mode.high': '고대비',
    'feature.contrast': '대비',
    'feature.textSize': '큰 글자',
    'feature.keyboardNav': '키보드 내비게이션',
    'feature.focusHighlight': '포커스 강조',
    'feature.linkHighlight': '링크 강조',
    'feature.animationStop': '애니메이션 중지',
    'feature.readingGuide': '읽기 가이드',
    'feature.readingMask': '읽기 마스크',
    'feature.hideImages': '이미지 숨기기',
    'feature.bigCursor': '큰 커서',
    'feature.pageStructure': '페이지 구조',
    'feature.tts': '소리 내어 읽기',
    'feature.textSimplify': '텍스트 단순화',
    'feature.altText': '이미지 설명',
    'feature.autoScan': 'WCAG 검사',
    'section.profiles': '빠른 프로필',
    'profile.visionImpaired': '시각 장애',
    'profile.visionImpaired.desc': '고대비, 큰 텍스트 및 커서',
    'profile.motorImpaired': '운동 장애',
    'profile.motorImpaired.desc': '키보드 탐색 및 포커스 지원',
    'profile.cognitive': '인지 지원',
    'profile.cognitive.desc': '읽기 지원, 간격 조정 및 간단한 글꼴',
    'profile.seizureSafe': '발작 방지',
    'profile.seizureSafe.desc': '애니메이션 중지 및 자극 감소',
    'profile.adhdFriendly': 'ADHD 친화적',
    'profile.adhdFriendly.desc': '방해 요소를 줄이고 집중력 향상',
  }),
};

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
];

const RTL_LANGS = new Set(['ar', 'ur']);
let currentWidgetLang = 'en';

export function getSupportedLang(lang?: string): string {
  if (!lang) return 'en';
  const normalized = lang.toLowerCase();
  if (locales[normalized]) return normalized;

  const base = normalized.split('-')[0];
  return locales[base] ? base : 'en';
}

export function setCurrentWidgetLang(lang?: string): void {
  currentWidgetLang = getSupportedLang(lang);
}

export function getCurrentWidgetLang(): string {
  return currentWidgetLang;
}

export function isRtlLang(lang?: string): boolean {
  return RTL_LANGS.has(getSupportedLang(lang));
}

export function t(key: string, lang: string = 'en'): string {
  const resolved = getSupportedLang(lang);
  return locales[resolved]?.[key] || EN_BASE[key] || key;
}

export function getLocale(lang: string): LocaleStrings {
  return locales[getSupportedLang(lang)] || EN_BASE;
}

// ---------------------------------------------------------------------------
// Lazy locale loading (R8-5 / R11-3) — fetch non-built-in locales from a
// translation proxy at runtime so we don't have to ship JSONs for every
// language the dashboard advertises. DE + EN stay bundled as the
// guaranteed offline-safe fallback. The proxy endpoint (separate ticket
// in packages/api) batch-translates EN_BASE via Google Translate and
// caches the result in KV — clients see ~120 key/value pairs per fetch.
//
// Fallback: if the proxy is unreachable, `t()` keeps returning EN_BASE
// strings — better than a blank UI.
// ---------------------------------------------------------------------------

const LOCALE_CACHE_PREFIX = 'accessify-locale-';
const LOCALE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const LOADING_LOCALES = new Set<string>();

interface CachedLocale {
  ts: number;
  strings: LocaleStrings;
}

function readCachedLocale(lang: string): LocaleStrings | null {
  try {
    const raw = localStorage.getItem(LOCALE_CACHE_PREFIX + lang);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedLocale;
    if (Date.now() - parsed.ts > LOCALE_CACHE_TTL_MS) {
      localStorage.removeItem(LOCALE_CACHE_PREFIX + lang);
      return null;
    }
    return parsed.strings;
  } catch {
    return null;
  }
}

function writeCachedLocale(lang: string, strings: LocaleStrings): void {
  try {
    localStorage.setItem(
      LOCALE_CACHE_PREFIX + lang,
      JSON.stringify({ ts: Date.now(), strings } satisfies CachedLocale),
    );
  } catch {
    // localStorage full / disabled — non-fatal, next session will refetch
  }
}

/**
 * Load a locale at runtime. No-op for built-in DE / EN. For everything
 * else: checks localStorage cache, then fetches the proxy translate
 * endpoint. On success the new strings join the `locales` map and a
 * `accessify:locale-loaded` event fires so components can re-render.
 */
export async function loadLocale(lang: string, proxyUrl?: string): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const normalized = lang.toLowerCase().split('-')[0];
  if (locales[normalized]) return true;
  if (LOADING_LOCALES.has(normalized)) return false;
  LOADING_LOCALES.add(normalized);

  // 1) Try the cache first (fast path, no network).
  const cached = readCachedLocale(normalized);
  if (cached) {
    locales[normalized] = { ...EN_BASE, ...cached };
    LOADING_LOCALES.delete(normalized);
    window.dispatchEvent(new CustomEvent('accessify:locale-loaded', { detail: { lang: normalized, source: 'cache' } }));
    return true;
  }

  // 2) Fetch from proxy. The endpoint contract is documented in
  //    `packages/api` — a flat key/value JSON object of LocaleStrings.
  const base = proxyUrl || 'https://api.goaccessify.de';
  try {
    const res = await fetch(`${base}/v1/locale/${encodeURIComponent(normalized)}`, {
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) {
      LOADING_LOCALES.delete(normalized);
      return false;
    }
    const data = await res.json() as { strings?: LocaleStrings };
    if (!data?.strings || typeof data.strings !== 'object') {
      LOADING_LOCALES.delete(normalized);
      return false;
    }
    locales[normalized] = { ...EN_BASE, ...data.strings };
    writeCachedLocale(normalized, data.strings);
    LOADING_LOCALES.delete(normalized);
    window.dispatchEvent(new CustomEvent('accessify:locale-loaded', { detail: { lang: normalized, source: 'proxy' } }));
    return true;
  } catch {
    // Network error — leave the EN fallback in place.
    LOADING_LOCALES.delete(normalized);
    return false;
  }
}

/** Check if a locale is bundled or already cached (no network call). */
export function isLocaleReady(lang: string): boolean {
  const normalized = (lang || '').toLowerCase().split('-')[0];
  return !!locales[normalized];
}
